import { createAccount, loginAccount, validateUsername } from './sogni.js';

const USERNAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9._-]{3,19}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let modal;
let onAuthenticated;
let mode = 'login';
let signupStep = 1;
let signup = {};
let turnstileWidget = null;
let scriptPromise = null;
let returnFocusTo = null;

const escapeHtml = (value) =>
  String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

export function validateLoginFields({ username, password }) {
  if (!username) return 'Enter your username.';
  if (!USERNAME_PATTERN.test(username)) {
    return 'Usernames are 4–20 characters and start with a letter.';
  }
  if (!password) return 'Enter your password.';
  return '';
}

export function validateSignupIdentity({ username, email }) {
  if (!USERNAME_PATTERN.test(username)) {
    return 'Choose a 4–20 character username that starts with a letter.';
  }
  if (!EMAIL_PATTERN.test(email)) return 'Enter a valid email address.';
  return '';
}

export function validateSignupPassword({ password, passwordConfirm, acknowledged }) {
  if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
    return 'Use at least 8 characters with both letters and numbers.';
  }
  if (password !== passwordConfirm) return 'The passwords do not match.';
  if (!acknowledged) return 'Confirm that you have safely stored your password.';
  return '';
}

function errorMessage(error, fallback) {
  const code = Number(error?.payload?.errorCode ?? error?.errorCode ?? error?.code);
  if (code === 105 || code === 128) return 'That username and password do not match.';
  return error?.payload?.message || error?.message || fallback;
}

function setBusy(busy, label) {
  const button = modal.querySelector('[data-primary]');
  if (!button) return;
  button.disabled = busy;
  button.dataset.original ||= button.textContent;
  button.textContent = busy ? label : button.dataset.original;
  modal.querySelectorAll('input, button').forEach((node) => {
    if (node !== modal.querySelector('[data-close]')) node.disabled = busy;
  });
}

function showError(message = '') {
  const box = modal.querySelector('[data-auth-error]');
  if (!box) return;
  box.textContent = message;
  box.classList.toggle('hidden', !message);
}

function field(name) {
  return modal.querySelector(`[name="${name}"]`);
}

function loginView() {
  return `
    <div class="auth-heading">
      <span class="auth-mark" aria-hidden="true">∞</span>
      <p class="auth-eyebrow">Sogni Infinity</p>
      <h2 id="auth-title">Welcome back</h2>
      <p>Sign in without leaving your pattern. Your renders use your own Spark or Unlimited plan.</p>
    </div>
    <form data-auth-form>
      <div class="auth-error hidden" data-auth-error role="alert"></div>
      <label class="auth-field"><span>Username</span>
        <input name="username" autocomplete="username" autocapitalize="none" spellcheck="false" required />
      </label>
      <label class="auth-field"><span>Password</span>
        <span class="auth-password"><input name="password" type="password" autocomplete="current-password" required />
          <button type="button" data-reveal aria-label="Show password">Show</button></span>
      </label>
      <label class="auth-check"><input name="remember" type="checkbox" checked /><span>Keep me signed in</span></label>
      <button class="auth-primary" type="submit" data-primary>Sign in</button>
    </form>
    <p class="auth-switch">New to Sogni? <button type="button" data-mode="signup">Create a free account</button></p>
  `;
}

function signupIdentityView() {
  return `
    ${signupHeader('Your account', 'Choose the name your public tiles will be credited to.')}
    <form data-auth-form>
      <div class="auth-error hidden" data-auth-error role="alert"></div>
      <label class="auth-field"><span>Username</span>
        <input name="username" value="${escapeHtml(signup.username || '')}" autocomplete="username" autocapitalize="none" spellcheck="false" required />
        <small>4–20 characters · letters, numbers, periods, hyphens, underscores</small>
      </label>
      <label class="auth-field"><span>Email</span>
        <input name="email" type="email" value="${escapeHtml(signup.email || '')}" autocomplete="email" required />
      </label>
      <label class="auth-check"><input name="subscribe" type="checkbox" ${signup.subscribe ? 'checked' : ''} /><span>Send me occasional Sogni news and releases</span></label>
      <label class="auth-field"><span>Referral code <em>optional</em></span>
        <input name="referralCode" value="${escapeHtml(signup.referralCode || '')}" autocomplete="off" />
      </label>
      <button class="auth-primary" type="submit" data-primary>Continue</button>
    </form>
    <p class="auth-switch">Already have an account? <button type="button" data-mode="login">Sign in</button></p>
  `;
}

function signupPasswordView() {
  return `
    ${signupHeader('Secure your account', 'Sogni credentials create your self-custodied wallet. Choose a password you can keep safe.')}
    <form data-auth-form>
      <div class="auth-error hidden" data-auth-error role="alert"></div>
      <input class="auth-visually-hidden" name="username" value="${escapeHtml(signup.username)}" autocomplete="username" />
      <label class="auth-field"><span>Password</span>
        <span class="auth-password"><input name="password" type="password" autocomplete="new-password" required />
          <button type="button" data-reveal aria-label="Show password">Show</button></span>
      </label>
      <label class="auth-field"><span>Confirm password</span>
        <input name="passwordConfirm" type="password" autocomplete="new-password" required />
      </label>
      <ul class="auth-requirements">
        <li>At least 8 characters</li><li>Contains letters and numbers</li><li>Both entries match</li>
      </ul>
      <div class="auth-warning"><strong>Your password cannot be reset.</strong> Save it in a password manager before continuing.</div>
      <label class="auth-check"><input name="acknowledged" type="checkbox" /><span>I understand and have safely stored my password</span></label>
      <label class="auth-check"><input name="remember" type="checkbox" checked /><span>Keep me signed in</span></label>
      <div class="auth-actions"><button type="button" class="auth-back" data-back>Back</button>
        <button class="auth-primary" type="submit" data-primary>Create account</button></div>
    </form>
  `;
}

function signupVerifyView() {
  return `
    ${signupHeader('One last step', 'Complete the quick security check, then we’ll create your Sogni account.')}
    <div class="auth-error hidden" data-auth-error role="alert"></div>
    <div class="auth-turnstile" data-turnstile><div class="auth-spinner" aria-label="Loading security check"></div></div>
    <p class="auth-legal">By creating an account, you agree to Sogni’s
      <a href="https://www.sogni.ai/privacy-policy" target="_blank" rel="noopener">Terms &amp; Privacy Policy</a>.</p>
    <button type="button" class="auth-back" data-back>Back</button>
  `;
}

function signupDoneView() {
  return `
    <div class="auth-success" role="status">
      <span class="auth-success-icon">✓</span>
      <p class="auth-eyebrow">The pattern starts here</p>
      <h2 id="auth-title">Welcome, @${escapeHtml(signup.username)}</h2>
      <p>Your account is ready. Verify your email to claim your free credits, then start making something impossible to stop looking at.</p>
      <button class="auth-primary" type="button" data-finish>Start tessellating</button>
    </div>
  `;
}

function signupHeader(title, text) {
  return `<div class="auth-heading"><span class="auth-mark" aria-hidden="true">∞</span>
    <p class="auth-eyebrow">Create account · ${signupStep} of 3</p><h2 id="auth-title">${title}</h2><p>${text}</p></div>`;
}

const SIGNUP_VIEWS = [
  null,
  signupIdentityView,
  signupPasswordView,
  signupVerifyView,
  signupDoneView
];

/**
 * The markup for a view. Split out of render() so it can be tested: the
 * signup steps used to be handed to innerHTML as function *references* rather
 * than calls, so every step of the signup flow printed the source of its own
 * view function into the dialog instead of a form.
 */
export function authViewHtml(viewMode = mode, step = signupStep) {
  if (viewMode === 'login') return loginView();
  // The headers read signupStep, so a caller asking for a specific step must
  // get that step's header too.
  const previous = signupStep;
  signupStep = step;
  try {
    return (SIGNUP_VIEWS[step] || signupIdentityView)();
  } finally {
    signupStep = previous;
  }
}

function render() {
  if (turnstileWidget != null && window.turnstile) {
    try {
      window.turnstile.remove(turnstileWidget);
    } catch {
      /* already removed */
    }
    turnstileWidget = null;
  }
  const content = modal.querySelector('[data-auth-content]');
  content.innerHTML = authViewHtml();
  bindView();
  if (mode === 'signup' && signupStep === 3) mountTurnstile();
  requestAnimationFrame(() =>
    modal.querySelector('input:not(.auth-visually-hidden), button[data-finish]')?.focus()
  );
}

function bindView() {
  modal.querySelector('[data-mode]')?.addEventListener('click', (event) => {
    mode = event.currentTarget.dataset.mode;
    signupStep = 1;
    render();
  });
  modal.querySelector('[data-back]')?.addEventListener('click', () => {
    signupStep = Math.max(1, signupStep - 1);
    render();
  });
  modal.querySelector('[data-finish]')?.addEventListener('click', close);
  modal.querySelector('[data-reveal]')?.addEventListener('click', (event) => {
    const input = event.currentTarget.closest('.auth-password').querySelector('input');
    input.type = input.type === 'password' ? 'text' : 'password';
    event.currentTarget.textContent = input.type === 'password' ? 'Show' : 'Hide';
  });
  modal.querySelector('[data-auth-form]')?.addEventListener('submit', submit);
}

async function submit(event) {
  event.preventDefault();
  showError();
  if (mode === 'login') {
    const payload = {
      username: field('username').value.trim(),
      password: field('password').value,
      remember: field('remember').checked
    };
    const invalid = validateLoginFields(payload);
    if (invalid) return showError(invalid);
    setBusy(true, 'Signing in…');
    try {
      await loginAccount(payload);
      await onAuthenticated();
      close();
    } catch (error) {
      showError(errorMessage(error, 'We could not sign you in. Please try again.'));
      field('password').value = '';
      setBusy(false);
    }
    return;
  }

  if (signupStep === 1) {
    const identity = {
      username: field('username').value.trim(),
      email: field('email').value.trim(),
      subscribe: field('subscribe').checked,
      referralCode: field('referralCode').value.trim()
    };
    const invalid = validateSignupIdentity(identity);
    if (invalid) return showError(invalid);
    setBusy(true, 'Checking username…');
    try {
      const result = await validateUsername(identity.username);
      if (result?.status === 'error') throw new Error(result.message);
      signup = { ...signup, ...identity };
      signupStep = 2;
      render();
    } catch (error) {
      showError(errorMessage(error, 'We could not check that username. Please try another.'));
      setBusy(false);
    }
    return;
  }

  const password = field('password').value;
  const passwordData = {
    password,
    passwordConfirm: field('passwordConfirm').value,
    acknowledged: field('acknowledged').checked
  };
  const invalid = validateSignupPassword(passwordData);
  if (invalid) return showError(invalid);
  signup = {
    ...signup,
    password,
    remember: field('remember').checked
  };
  signupStep = 3;
  render();
}

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('The security check could not load.'));
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

async function mountTurnstile() {
  try {
    await loadTurnstile();
    if (signupStep !== 3 || modal.classList.contains('hidden')) return;
    const host = modal.querySelector('[data-turnstile]');
    host.innerHTML = '';
    turnstileWidget = window.turnstile.render(host, {
      sitekey: modal.dataset.turnstileKey,
      theme: 'dark',
      callback: completeSignup,
      'error-callback': () => showError('The security check failed. Please try again.'),
      'expired-callback': () => showError('The security check expired. Please complete it again.')
    });
  } catch (error) {
    showError(error.message);
  }
}

async function completeSignup(token) {
  showError();
  modal.querySelector('[data-turnstile]').classList.add('is-busy');
  try {
    await createAccount({ ...signup, turnstileToken: token });
    await onAuthenticated();
    signupStep = 4;
    render();
  } catch (error) {
    showError(errorMessage(error, 'We could not create your account. Please try again.'));
    modal.querySelector('[data-turnstile]')?.classList.remove('is-busy');
    if (turnstileWidget != null) window.turnstile.reset(turnstileWidget);
  }
}

function close() {
  modal.classList.add('hidden');
  document.body.classList.remove('auth-open');
  if (returnFocusTo?.isConnected) returnFocusTo.focus();
  returnFocusTo = null;
}

export function openAuthModal(initialMode = 'login') {
  returnFocusTo = document.activeElement;
  mode = initialMode;
  signupStep = 1;
  signup = {};
  modal.classList.remove('hidden');
  document.body.classList.add('auth-open');
  render();
}

export function initAuthModal({ authenticated, turnstileSiteKey }) {
  modal = document.getElementById('auth-modal');
  onAuthenticated = authenticated;
  modal.dataset.turnstileKey = turnstileSiteKey;
  modal.querySelector('[data-close]').addEventListener('click', close);
  modal.addEventListener('click', (event) => {
    if (event.target === modal) close();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.classList.contains('hidden')) close();
  });
}
